import React, { useState, useMemo } from 'react';
import { Card, Table, Typography, Slider, InputNumber, Row, Col, Space, Tag, Tooltip, Progress, Select, Button, Modal } from 'antd';
import ServiceAnalysis from './ServiceAnalysis';
import { processStopData } from '../../../utils/stopDataProcessor';
import {
  normalModel,
  empiricalModel,
  logNormalModel,
  weibullModel,
  robustModel
} from '../../../utils/statisticalModels';

const { Title, Text } = Typography;
const { Option } = Select;

// Helper: Format time as HH:MM
const formatTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  const timePart = timeStr.split(' ')[1] || timeStr;
  const [h, m] = timePart.split(':');
  return `${h}:${m}`;
};

// Helper: Get trip start time from scheduledTime
const getTripStartTime = (tripId, stopData) => {
  const tripStops = stopData.filter(item => item.tripId === tripId);
  if (tripStops.length === 0) return null;
  const firstStop = tripStops.reduce((earliest, stop) => {
    if (!earliest) return stop;
    return stop.scheduledTime < earliest.scheduledTime ? stop : earliest;
  }, null);
  if (!firstStop || !firstStop.scheduledTime) return null;
  const timePart = firstStop.scheduledTime.split(' ')[1];
  if (!timePart) return null;
  const [h, m] = timePart.split(':');
  return `${h}:${m}`;
};

const ProposedOTRWindowTimetables = ({ reportData, dateRange }) => {
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState('all');
  const [showServiceAnalysis, setShowServiceAnalysis] = useState(false);

  // Extract stop-level data using the utility function
  const stopData = useMemo(() => processStopData(reportData), [reportData]);

  // Get unique routes and trips for filters
  const routes = useMemo(() => [...new Set(stopData.map(item => item.routeNo))].sort(), [stopData]);
  const tripOptions = useMemo(() => {
    let filtered = stopData;
    if (selectedRoute !== 'all') filtered = filtered.filter(item => item.routeNo === selectedRoute);
    // Get unique tripIds
    const tripIds = [...new Set(filtered.map(item => item.tripId))];
    // For each tripId, find the earliest scheduled time
    const options = tripIds.map(tripId => {
      const stopsForTrip = filtered.filter(item => item.tripId === tripId);
      const earliest = stopsForTrip.reduce((min, stop) => {
        if (!min) return stop;
        return stop.scheduledTime < min.scheduledTime ? stop : min;
      }, null);
      let startTime = '';
      if (earliest && earliest.scheduledTime) {
        const timePart = earliest.scheduledTime.split(' ')[1];
        if (timePart) {
          const [h, m] = timePart.split(':');
          startTime = `${h}:${m}`;
        }
      }
      return { tripId, label: `Trip ${tripId}${startTime ? ` (${startTime})` : ''}`, startTime };
    });
    // Sort by start time
    return options.sort((a, b) => {
      const [aH, aM] = a.startTime.split(':').map(Number);
      const [bH, bM] = b.startTime.split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    });
  }, [stopData, selectedRoute]);

  // Filter stopData by selected route and trip
  const filteredStopData = useMemo(() => {
    let filtered = stopData;
    if (selectedRoute !== 'all') filtered = filtered.filter(item => item.routeNo === selectedRoute);
    if (selectedTrip !== 'all') {
      filtered = filtered.filter(item => (item.tripId || '') === selectedTrip);
    }
    return filtered;
  }, [stopData, selectedRoute, selectedTrip]);

  const formatAdjustment = (minutes) => {
    if (minutes === 0) return 'No change';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    let formatted = '';
    if (hours > 0) formatted += `${hours}h `;
    if (mins > 0 || hours === 0) formatted += `${mins}m`;
    return minutes > 0 ? `+${formatted}` : `-${formatted}`;
  };
  const getAdjustmentColor = (minutes) => {
    if (minutes === 0) return 'default';
    if (minutes > 0) return 'blue';
    return 'red';
  };

  const columns = [
    { 
      title: <Tooltip title="The route number for this service">Route</Tooltip>, 
      dataIndex: 'routeNo', 
      key: 'routeNo', 
      width: 100, 
      render: text => <Tag color="blue">{text}</Tag>,
      sorter: (a, b) => (a.routeNo || '').localeCompare(b.routeNo || '') 
    },
    { 
      title: <Tooltip title="The trip identifier and its scheduled start time">Trip</Tooltip>, 
      dataIndex: 'tripId', 
      key: 'tripId', 
      width: 120, 
      render: (text, record) => {
        const startTime = getTripStartTime(text, stopData);
        return <Tag color="purple">{startTime ? startTime : text}</Tag>;
      },
      sorter: (a, b) => {
        // Sort by tripId, then by start time
        if (a.tripId === b.tripId) {
          const aStart = getTripStartTime(a.tripId, stopData) || '';
          const bStart = getTripStartTime(b.tripId, stopData) || '';
          return aStart.localeCompare(bStart);
        }
        return (a.tripId || '').localeCompare(b.tripId || '');
      }
    },
    { 
      title: <Tooltip title="The stop identifier and name">Stop</Tooltip>, 
      dataIndex: 'stopId', 
      key: 'stopId', 
      width: 100, 
      render: (text, record) => (
        <Tooltip title={record.stopName}><Text>{text}</Text></Tooltip>
      ) 
    },
    { 
      title: <Tooltip title="Current On-Time Running percentage for this stop">Current OTR</Tooltip>, 
      dataIndex: 'currentOTR', 
      key: 'currentOTR', 
      width: 120, 
      render: v => (
        <Progress percent={Math.round(v || 0)} size="small" status={v >= 95 ? 'success' : v >= 90 ? 'normal' : 'exception'} format={percent => `${percent.toFixed(1)}%`} />
      ),
      sorter: (a, b) => (a.currentOTR || 0) - (b.currentOTR || 0)
    },
    { 
      title: <Tooltip title="Proposed time adjustment to improve On-Time Running">Proposed Adjustment</Tooltip>, 
      key: 'adjustment', 
      width: 160, 
      render: (_, record) => (
        <Tag color={getAdjustmentColor(Math.round((record.shiftSeconds || 0) / 60))}>{formatAdjustment(Math.round((record.shiftSeconds || 0) / 60))}</Tag>
      ) 
    },
    { 
      title: <Tooltip title="Currently scheduled arrival/departure time">Current Time</Tooltip>, 
      dataIndex: 'scheduledTime', 
      key: 'scheduledTime', 
      width: 120, 
      render: text => <Text>{formatTime(text)}</Text>,
      sorter: (a, b) => {
        const getTimeInMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const timePart = timeStr.split(' ')[1] || timeStr;
          const [h, m] = timePart.split(':').map(Number);
          return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
        };
        return getTimeInMinutes(a.scheduledTime) - getTimeInMinutes(b.scheduledTime);
      }
    },
    { 
      title: <Tooltip title="Proposed new arrival/departure time after adjustment">Proposed Time</Tooltip>, 
      dataIndex: 'proposedTime', 
      key: 'proposedTime', 
      width: 120, 
      render: text => <Tag color="purple">{formatTime(text)}</Tag> 
    },
    { 
      title: <Tooltip title="Projected On-Time Running percentage after implementing the proposed time adjustment">Resulting OTR</Tooltip>, 
      dataIndex: 'percentInWindow', 
      key: 'percentInWindow', 
      width: 140, 
      render: v => (
        <Progress
          percent={Math.round(v || 0)}
          size="small"
          status={v >= 95 ? 'success' : v >= 90 ? 'normal' : 'exception'}
          format={percent => `${percent.toFixed(1)}%`}
        />
      ),
      sorter: (a, b) => (a.percentInWindow || 0) - (b.percentInWindow || 0)
    }
  ];

  return (
    <Card title="Proposed OTR Window Timetables">
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={11}>
            <Select
              value={selectedRoute}
              onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
              style={{ width: '100%' }}
              placeholder="Select route"
            >
              <Option value="all">All Routes</Option>
              {routes.map(route => (
                <Option key={route} value={route}>Route {route}</Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <Select
              value={selectedTrip}
              onChange={value => setSelectedTrip(value)}
              style={{ width: '100%' }}
              placeholder="Select trip"
              disabled={selectedRoute === 'all'}
            >
              <Option value="all">All Trips</Option>
              {tripOptions.map(opt => (
                <Option key={opt.tripId} value={opt.tripId}>{opt.label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={2} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              onClick={() => setShowServiceAnalysis(true)}
              disabled={selectedRoute === 'all' || selectedTrip === 'all'}
            >
              Service Analysis
            </Button>
          </Col>
        </Row>
      </div>
      <Table
        dataSource={filteredStopData}
        columns={columns}
        rowKey={r => `${r.routeNo}-${r.tripId}-${r.stopId}`}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 900 }}
      />
      <Modal
        title="Service Analysis"
        open={showServiceAnalysis}
        onCancel={() => setShowServiceAnalysis(false)}
        width={1200}
        footer={null}
        destroyOnClose
      >
        <ServiceAnalysis
          dateRange={dateRange}
          reportData={reportData}
          proposedOTRWindowTimetables={stopData}
          selectedRoute={selectedRoute}
          selectedTrip={selectedTrip}
          setSelectedRoute={setSelectedRoute}
          setSelectedTrip={setSelectedTrip}
        />
      </Modal>
    </Card>
  );
};

export default ProposedOTRWindowTimetables; 